import { useContext, useEffect, useRef, useState } from "react";

import { useChatAssistantAttachments } from "@/contexts/useChatAssistantAttachments";
import { COMMODITY_NAME_CORN } from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import ArrowDropDownOutlined from "@mui/icons-material/ArrowDropDownOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Aggregate,
  AggregateColumnDirective,
  AggregateColumnsDirective,
  AggregateDirective,
  AggregatesDirective,
  ColumnDirective,
  ColumnsDirective,
  ExcelExport,
  Filter,
  GridComponent,
  Group,
  Inject,
  Page,
  PdfExport,
  Sort,
  Toolbar,
} from "@syncfusion/ej2-react-grids";
import { getBudgetYears } from "components/Budget/helpers";
import DataFormSelect from "components/Core/DataFormSelect";
//import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import DataGridContext from "core/DataGridContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { ChatAssistantButton } from "mui-toolkit/ChatAssistantButton";
import { formatNumber } from "utility/formatters";

import ResponsiveReportCardGrid from "../../shared/DataGrid/ResponsiveReportCardGrid";

const defaultPageSize = 20;
const pageSizes = [5, 10, 15, 20, 50, 100, 200, 1000];
const VERTICAL_TABLE_LINE_MARGIN = 5;
const VERTICAL_TABLE_HEADER_CELL_WIDTH = 60;

export const ReportViewer = ({
  columnsDirective,
  counties,
  producerFarmFieldCrops,
  isLoadingCounties,
  reportType,
  setIsDetailViewOnFunc,
  currentYear,
  setCurrentYear,
  gridKey,
  filter,
  dispatchFilter,
  isVerticalPdfExport = false,
}) => {
  const [filterLocationStateName, setFilterLocationStateName] = useState(null);
  const [filterLocationCountyName, setFilterLocationCountyName] = useState(null);
  const [chatResponse, setChatResponse] = useState(null);
  const { addAttachments } = useChatAssistantAttachments();

  // ---
  const { getGridState, setGridState } = useContext(DataGridContext);
  const theme = useTheme();

  const filterHandleChangeState = (event, value) => {
    value = value ?? event.target.value;

    dispatchFilter({
      type: "update",
      payload: { key: event.target.name, value: value },
    });
  };

  const handleActionComplete = (args) => {
    if (args.requestType === "sorting" || args.requestType === "paging") {
      if (args.requestType === "sorting") {
        setGridState(
          gridKey,
          args.columnName && args.direction ? { sorting: { field: args.columnName, sort: args.direction } } : null
        );
      }

      if (args.requestType === "paging") {
        setGridState(gridKey, { pageSize: args.pageSize });
      }
    }
  };

  const captionTemplate = (props) => {
    let groupLabel = props?.field;
    let groupValue = "";
    let groupCount = 0;
    if (props?.items.records?.length && props?.items.records[0] && props?.items.records[0][props?.field]) {
      groupValue = props?.items.records[0][props?.field];
    } else if (props?.items?.length && props.items[0]) {
      groupValue = props?.items[0][props?.field];
    }

    return (
      <span className="groupItems">
        <label onClick={() => setFilter(groupLabel, groupValue)}>
          <strong>{groupValue}</strong> ({groupCount})
        </label>
      </span>
    );
  };

  const [isDetailViewOn, setIsDetailViewOn] = useState(false);

  const setFilter = (groupLabel, groupValue) => {
    if (groupLabel === "location_state_name") {
      setFilterLocationStateName(groupValue);
      setFilterLocationCountyName(null);
    } else if (groupLabel === "location_county_name") {
      setFilterLocationCountyName(groupValue);
      setFilterLocationStateName(null);
    }
  };

  useEffect(() => {
    if (filterLocationStateName || filterLocationCountyName) {
      setIsDetailViewFun(false);
    }
  }, [filterLocationStateName, filterLocationCountyName]);

  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("md"), { noSsr: true });
  const { component: confirmDialogComponent } = useDialog();

  const gridRef = useRef(null);

  const [allProducerFarmCrops, setAllProducerFarmCrops] = useState([]);

  useEffect(() => {
    // Reducer counties[ii].ProducerFarmCrops
    const _producerFarmCrops = producerFarmFieldCrops?.filter((pfc) => {
      const isCountyMatch = filterLocationCountyName === null || pfc?.location_county_name === filterLocationCountyName;
      const isStateMatch = filterLocationStateName === null || pfc?.location_state_name === filterLocationStateName;
      return isCountyMatch && isStateMatch;
    });

    setAllProducerFarmCrops(_producerFarmCrops);
    addAttachments([{ name: reportType, content: _producerFarmCrops }]);
  }, [producerFarmFieldCrops, filterLocationStateName, filterLocationCountyName]);

  // Template for group footer aggregate
  const groupFooterAverageTemplate = (props) => {
    return (
      <span>
        Avg: <strong>{formatNumber(props.Average, 0)}</strong>
      </span>
    );
  };

  const handleGridToolbarClick = async (args) => {
    const grid = gridRef.current;
    if (!grid) return;
    if (grid && args?.item?.id?.toLowerCase().includes("excel")) {
      grid.excelExport();
    } else if (grid && args?.item?.id?.toLowerCase().includes("pdf")) {
      if (isVerticalPdfExport) exportVerticalTablePDF(grid);
      else grid.pdfExport();
    }
  };

  const exportVerticalTablePDF = (grid) => {
    const doc = new jsPDF();
    let startY = 10;

    grid.dataSource.forEach((record, index) => {
      const tableBody = grid.columns
        .filter((col) => col.visible !== false && col.allowPdfExport !== false)
        .map((col) => [col.headerText || col.field, record[col.field] ?? ""]);

      autoTable(doc, {
        body: tableBody,
        startY: startY,
        theme: "grid",
        columnStyles: {
          0: { fontStyle: "bold", cellWidth: VERTICAL_TABLE_HEADER_CELL_WIDTH },
          1: { cellWidth: "auto" },
        },
        styles: {
          fontSize: 11,
          cellPadding: 4,
        },
      });

      const tableBottomY = doc.lastAutoTable.finalY;

      if (index < grid.dataSource.length - 1) {
        const lineY = tableBottomY + VERTICAL_TABLE_LINE_MARGIN;
        doc.setDrawColor(200, 200, 200);
        doc.line(
          doc.lastAutoTable.settings.margin.left,
          lineY,
          doc.internal.pageSize.getWidth() - doc.lastAutoTable.settings.margin.right,
          lineY
        );
        startY = lineY + VERTICAL_TABLE_LINE_MARGIN;
      }
    });

    doc.save(gridKey ? `${gridKey}.pdf` : "Export.pdf");
  };

  const pageOptionsSummary = {
    pageSize: getGridState()[gridKey]?.pageSize || defaultPageSize,
    pageSizes: pageSizes,
  };
  const pageOptionsDetail = {
    pageSize: getGridState()[gridKey]?.pageSize || defaultPageSize,
    pageSizes: pageSizes,
  };

  // ---

  const setIsDetailViewFun = (val) => {
    setIsDetailViewOn(val);
    if (setIsDetailViewOnFunc) {
      setIsDetailViewOnFunc(val);
    }
  };

  const handleChatResponse = (records) => {
    // NOOP.
  };
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 1, pageSize: 10 });

  return (
    <>
      <Grid container className="navigation" spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6} lg={4}>
          <DataFormSelect
            value={filter.commodity_name || ""}
            name="commodity_name"
            isRequired={false}
            onChange={filterHandleChangeState}
            objectName="Commodity"
            itemFilter={{ reinsurance_year: String(currentYear) }}
            itemGetKey={(item) => item.commodity_name}
            itemGetLabel={(item) => item.commodity_name}
            doFetch={currentYear !== null}
            isLoading={isLoadingCounties}
            multiple={true}
            limitTags={isSmall ? 2 : 3}
            autoSelect={false}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <DataFormSelect
            value={filter.practice_name || ""}
            name="practice_name"
            isRequired={false}
            onChange={filterHandleChangeState}
            objectName="Practice"
            itemFilter={{
              reinsurance_year: String(currentYear),
              commodity_name: filter.commodity_name[0] || COMMODITY_NAME_CORN,
            }}
            itemGetKey={(item) => item.practice_name}
            itemGetLabel={(item) => item.practice_name}
            doFetch={currentYear !== null}
            isLoading={isLoadingCounties}
            multiple={true}
            limitTags={isSmall ? 2 : 3}
            autoSelect={false}
          />
        </Grid>
      </Grid>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{ mb: 2 }}
        className="no-print"
      >
        <Box className="report_viewer" sx={{ display: "flex", justifyContent: "flex-start", gap: 2, flexWrap: "wrap" }}>
          <Button
            onClick={() => setIsDetailViewFun(!isDetailViewOn)}
            variant="outlined"
            endIcon={isDetailViewOn ? <ExpandLess /> : <ExpandMore />}
          >
            Detail
          </Button>

          <PopupState variant="popover" popupId="demo-popup-menu">
            {(popupState) => (
              <>
                <Button variant="outlined" {...bindTrigger(popupState)} className="year-filter">
                  {currentYear} <ArrowDropDownOutlined />
                </Button>
                <Menu {...bindMenu(popupState)}>
                  {getBudgetYears()?.map((year, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        setCurrentYear(year);
                        popupState.close();
                      }}
                    >
                      {year}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </PopupState>

          {filterLocationStateName && (
            <Chip label={filterLocationStateName} onDelete={() => setFilterLocationStateName(null)} />
          )}
          {filterLocationCountyName && (
            <Chip label={filterLocationCountyName} onDelete={() => setFilterLocationCountyName(null)} />
          )}
          <ChatAssistantButton
            systemPrompt="tilley_my_farm_data"
            onChangeRecords={handleChatResponse}
            placeholder="Ask Tilley..."
          />
        </Box>
      </Stack>

      {chatResponse && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: "normal", whiteSpace: "pre-wrap" }}>
            {chatResponse}
          </Typography>
        </Alert>
      )}

      {!!isLoadingCounties && <CircularProgress />}

      {!isLoadingCounties && (counties?.length > 0 || allProducerFarmCrops?.length > 0) && (
        <>
          <GridComponent
            cssClass={"tilley " + (isDetailViewOn ? "tilley-grid-detail" : "tilley-grid-summary")}
            style={{ color: theme.palette.primary.light }}
            dataSource={allProducerFarmCrops}
            allowSorting={true}
            allowFiltering={isDetailViewOn}
            allowGrouping={true}
            allowPaging={true}
            pageSettings={isDetailViewOn ? pageOptionsDetail : pageOptionsSummary}
            sortSettings={
              getGridState()[gridKey]?.sorting
                ? {
                    columns: [
                      {
                        field: getGridState()[gridKey]?.sorting.field,
                        direction: getGridState()[gridKey]?.sorting.sort,
                      },
                    ],
                  }
                : { columns: [] }
            }
            toolbar={["ExcelExport", "PdfExport"]}
            toolbarClick={handleGridToolbarClick}
            allowPdfExport={true}
            allowExcelExport={true}
            groupSettings={{
              showDropArea: isDetailViewOn,
              columns: isDetailViewOn
                ? [
                    "location_state_name",
                    "location_county_name",
                    ...(reportType === "insurance" ? ["commodity_name"] : []),
                  ]
                : [],
              // Header:
              captionTemplate: captionTemplate,
            }}
            ref={(g) => (gridRef.current = g)}
            actionComplete={handleActionComplete}
          >
            <ColumnsDirective>
              {columnsDirective?.map((item, index) => (
                <ColumnDirective key={index} {...item} />
              ))}
            </ColumnsDirective>
            <AggregatesDirective>
              <AggregateDirective>
                <AggregateColumnsDirective>
                  <AggregateColumnDirective
                    field="approved_yield"
                    type="Average"
                    groupFooterTemplate={isDetailViewOn ? groupFooterAverageTemplate : "<span/>"}
                  />
                </AggregateColumnsDirective>
              </AggregateDirective>
            </AggregatesDirective>
            <Inject services={[Sort, Filter, Group, Aggregate, Page, Toolbar, ExcelExport, PdfExport]} />
          </GridComponent>
          {isSmall && !isDetailViewOn && (
            <ResponsiveReportCardGrid
              columns={columnsDirective}
              rows={producerFarmFieldCrops}
              loading={isLoadingCounties}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
            />
          )}
        </>
      )}

      {confirmDialogComponent}
    </>
  );
};
